import { useState } from 'react';
import { ChevronDown, Package, Plus, Sparkles } from 'lucide-react';
import { MoneyValue } from './MoneyValue';
import { cn } from '@/lib/utils';
import type { Bundle } from '@/types/coupon';

interface BundleCardProps {
  bundle: Bundle;
  onSelect: (bundle: Bundle) => void;
  /** Stagger index for the load reveal. */
  index?: number;
  /** Date-resolved price for the visit date; overrides the base display. */
  resolvedPrice?: { price: number; originalPrice?: number; savings?: number };
}

/** Catalog tile for a ticket bundle — heritage crimson/gold, with an
 *  expandable "what's inside" list and the bundle savings called out. */
export function BundleCard({ bundle, onSelect, index = 0, resolvedPrice }: BundleCardProps) {
  const [open, setOpen] = useState(false);
  const soldOut = bundle.outOfStock;
  // Date-aware price the sale will charge; fall back to the base bundle price.
  const price = typeof resolvedPrice?.price === 'number' ? resolvedPrice.price : bundle.price;
  const originalPrice =
    typeof resolvedPrice?.originalPrice === 'number'
      ? resolvedPrice.originalPrice
      : bundle.originalPrice;
  const hasSavings = typeof originalPrice === 'number' && originalPrice > price;
  const savings =
    resolvedPrice?.savings ?? bundle.savings ?? (hasSavings ? originalPrice! - price : 0);
  const itemUnits = bundle.items.reduce((s, it) => s + (it.quantity ?? 1), 0);

  return (
    <div
      style={{ animationDelay: `${Math.min(index, 16) * 28}ms` }}
      className={cn(
        'group relative flex animate-reveal-up flex-col overflow-hidden rounded-lg border-2 border-gold-600/40 bg-card text-left shadow-sm transition-all',
        'hover:border-gold-600 hover:shadow-pop',
        soldOut && 'opacity-60',
      )}
    >
      {/* Header band — heritage crimson with a gold package mark */}
      <div className="relative flex items-center gap-3 overflow-hidden rail-surface px-4 py-3 text-cream-50">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              'linear-gradient(45deg, #FFD700 25%, transparent 25% 75%, #FFD700 75%)',
            backgroundSize: '18px 18px',
            backgroundPosition: '0 0, 9px 9px',
          }}
        />
        <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/10 ring-1 ring-gold-500/30">
          <Package className="h-5 w-5 text-gold-500" />
        </span>
        <div className="relative min-w-0 flex-1">
          <span className="block font-brand text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-gold-200">
            Paket Tiket
          </span>
          <h3 className="line-clamp-1 font-display text-base font-bold leading-tight">
            {bundle.name}
          </h3>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        {bundle.description && (
          <p className="line-clamp-2 text-xs leading-relaxed text-ink-600">{bundle.description}</p>
        )}

        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <MoneyValue value={price} emphasis className="text-xl" />
          {hasSavings && <MoneyValue value={originalPrice} strike className="text-xs" />}
        </div>

        {savings > 0 && (
          <span className="inline-flex w-fit items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 text-[11px] font-semibold text-success">
            <Sparkles className="h-3 w-3" /> Hemat <MoneyValue value={savings} className="text-[11px] text-success" />
          </span>
        )}

        {/* Contents toggle */}
        {bundle.items.length > 0 && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-1 inline-flex w-fit items-center gap-1 text-xs font-semibold text-primary hover:underline"
            aria-expanded={open}
          >
            {open ? 'Sembunyikan isi' : `Lihat isi paket · ${itemUnits} tiket`}
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
          </button>
        )}
        {open && (
          <ul className="space-y-1 rounded-md bg-secondary/60 p-2.5">
            {bundle.items.map((it, i) => (
              <li
                key={`${it.productId}-${i}`}
                className="flex items-center justify-between gap-2 text-xs text-ink-600"
              >
                <span className="min-w-0 truncate text-ink-900">
                  {it.name ?? 'Item paket'}
                </span>
                <span className="tnum shrink-0 text-ink-400">× {it.quantity ?? 1}</span>
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          disabled={soldOut}
          onClick={() => onSelect(bundle)}
          className={cn(
            'mt-auto flex h-11 items-center justify-center gap-2 rounded-md bg-primary text-sm font-semibold text-primary-foreground transition-colors',
            'hover:bg-crimson-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {soldOut ? (
            'Stok habis'
          ) : (
            <>
              <Plus className="h-4 w-4" /> Tambah paket
            </>
          )}
        </button>
      </div>
    </div>
  );
}
